import { Component, OnDestroy, OnInit } from '@angular/core';
import { ProjectsService } from 'src/app/modules/shared/services/projects.service';
import { ApiEstimativaService } from 'src/app/modules/shared/services/api-estimativa.service';
import { LanguageService } from 'src/app/modules/shared/services/language.service';
import { UsersManagementService } from 'src/app/modules/shared/services/usersManagement.service';
import * as echarts from 'echarts';
import { FormBuilder, Validators } from '@angular/forms';
import { formatDate } from '@angular/common';
import { ScrollControlService } from '../../shared/services/scroll-control.service';
import { AuthService } from '../../shared/services/auth.service';
import { forkJoin, Subject } from 'rxjs';
import { retryWhen, catchError, scan, delay, filter, switchMap, take, takeUntil } from 'rxjs/operators';
import { EstimationState } from 'src/app/models/EstimationState';

@Component({
  standalone: false,
  selector: 'app-stats',
  templateUrl: './stats.component.html',
  styleUrls: ['./stats.component.css']
})
export class StatsComponent implements OnInit, OnDestroy {
  variables: any;
  private readonly destroy$ = new Subject<void>();
  formGroup1: any;
  estimates: any[] = [];
  imputations: any[] = [];
  years: any[] = [];

  // KPIs
  totalEstimates = 0;
  totalHours = 0;
  avgPrecision: number | null = null;
  imputationCoverage = 0;
  hasImputationData = false;
  heatmapYear = '';
  deviationTotalCount = 0;

  // Color palette
  private readonly COLORS = ['#6366F1', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#06B6D4', '#F43F5E', '#84CC16'];
  private readonly MONTHS_KEYS = ['janeiro', 'fevereiro', 'marco', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

  constructor(
    private projectsService: ProjectsService,
    public authService: AuthService,
    private estimatesService: ApiEstimativaService,
    private languageService: LanguageService,
    private usersManagementService: UsersManagementService,
    private _formBuilder: FormBuilder,
    private estimativaService: ApiEstimativaService,
    private scrollService: ScrollControlService
  ) { }

  public estimationState = EstimationState;

  ngOnInit(): void {
    this.scrollService.setScroll(true);
    this.languageService.translations$
      .pipe(takeUntil(this.destroy$))
      .subscribe((vars: any) => {
        this.variables = vars;
        if (this.estimates.length > 0) {
          this.drawAllCharts();
          if (this.hasImputationData) {
            this.drawDeviationChart();
            this.drawCatalogPrecisionBars();
          }
        }
      });

    this.formGroup1 = this._formBuilder.group({
      year1Ctrl: ['', Validators.required],
      heatmapCtrl: ['technology', Validators.required],
      heatmapYearCtrl: ['', Validators.required]
    });

    this.authService.userDataLoaded$.pipe(
      filter(loaded => loaded),
      take(1),
      switchMap(() => this.authService.getUserData().pipe(take(1)))
    ).subscribe(result => {

      this.estimativaService.myEstimates().subscribe((estimates: any[]) => {
        this.estimates = estimates;
        this.setAllYears(estimates);
        this.computeKPIs();
        this.loadImputationsAndDraw();
        this.drawAllCharts();
      });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ===== Data helpers =====

  private setAllYears(estimates: any[]): void {
    estimates.forEach((e: any) => {
      const year = e.createdDate?.slice(6, 10);
      if (year && !this.years.includes(year)) this.years.push(year);
    });
    this.years.sort((a: string, b: string) => a.localeCompare(b));
    if (this.years.length > 0) {
      this.formGroup1.get('year1Ctrl').setValue(this.years[this.years.length - 1]);
    }
  }

  private getMonths(): string[] {
    return this.MONTHS_KEYS.map(k => this.variables?.[k] || k);
  }

  private filterByYear(data: any[], year: string): any[] {
    return data.filter((e: any) => e.createdDate?.slice(6, 10) === year);
  }

  private isCurrentYear(year: string): boolean {
    return year === formatDate(new Date(), 'yyyy', 'en');
  }

  private truncateToCurrentMonth(arr: any[], year: string): any[] {
    if (this.isCurrentYear(year)) {
      const m = Number(formatDate(new Date(), 'MM', 'en'));
      return arr.slice(0, m);
    }
    return arr;
  }

  // ===== KPIs =====

  private computeKPIs(): void {
    this.totalEstimates = this.estimates.length;
    this.totalHours = this.estimates.reduce((sum: number, e: any) => sum + (e.totalEffortHours || 0), 0);
  }

  private computeImputationKPIs(): void {
    if (this.imputations.length === 0) {
      this.avgPrecision = null;
      this.imputationCoverage = 0;
      this.hasImputationData = false;
      return;
    }

    this.hasImputationData = true;
    this.imputationCoverage = this.totalEstimates > 0
      ? (this.imputations.length / this.totalEstimates) * 100
      : 0;

    // Precision: actual / estimated for each imputation matched to its estimate
    let precisionSum = 0;
    let precisionCount = 0;
    this.imputations.forEach((imp: any) => {
      const est = this.estimates.find((e: any) => e.id === imp.estimateId);
      if (est && est.totalEffortHours > 0 && imp.totalActualHours != null) {
        precisionSum += imp.totalActualHours / est.totalEffortHours;
        precisionCount++;
      }
    });
    this.avgPrecision = precisionCount > 0 ? precisionSum / precisionCount : null;
  }

  // ===== Load imputations =====

  private loadImputationsAndDraw(): void {
    this.estimativaService.getAllImputations().subscribe({
      next: (imps: any[]) => {
        this.imputations = imps || [];
        this.computeImputationKPIs();
        this.drawDeviationChart();
        this.drawCatalogPrecisionBars();
      },
      error: () => {
        this.imputations = [];
        this.computeImputationKPIs();
      }
    });
  }

  // ===== Draw all charts =====

  private drawAllCharts(): void {
    const latestYear = this.years.length > 0 ? this.years[this.years.length - 1] : '';
    const yearEstimates = this.filterByYear(this.estimates, latestYear);

    this.heatmapYear = latestYear;
    this.formGroup1.get('heatmapYearCtrl')?.setValue(latestYear);

    setTimeout(() => {
      this.drawActivityChart(yearEstimates, latestYear);
      this.drawStateDonut(this.estimates);
      this.drawCatalogTreemap(this.estimates);
      this.drawHeatmap(this.filterByYear(this.estimates, latestYear), 'technology');
    }, 100);
  }

  // ===== Event handlers =====

  onChangeActivityYear(year: string): void {
    const data = this.filterByYear(this.estimates, year);
    this.drawActivityChart(data, year);
  }

  onChangeHeatmapMode(mode: string): void {
    const data = this.heatmapYear ? this.filterByYear(this.estimates, this.heatmapYear) : this.estimates;
    this.drawHeatmap(data, mode);
  }

  onChangeHeatmapYear(year: string): void {
    this.heatmapYear = year;
    const mode = this.formGroup1.get('heatmapCtrl')?.value || 'technology';
    this.drawHeatmap(this.filterByYear(this.estimates, year), mode);
  }

  // ===== Graph 1: Activity Timeline =====

  private drawActivityChart(data: any[], year: string): void {
    const months = this.truncateToCurrentMonth([...this.getMonths()], year);
    const countPerMonth = new Array(12).fill(0);
    const hoursPerMonth = new Array(12).fill(0);

    data.forEach((e: any) => {
      const m = Number(e.createdDate?.slice(3, 5)) - 1;
      if (m >= 0 && m < 12) {
        countPerMonth[m]++;
        hoursPerMonth[m] += e.totalEffortHours || 0;
      }
    });

    const truncCount = this.truncateToCurrentMonth(countPerMonth, year);
    const truncHours = this.truncateToCurrentMonth(hoursPerMonth, year);

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' }
      },
      legend: { data: [this.variables?.estimates || 'Estimaciones', this.variables?.effort || 'Esfuerzo (h)'], bottom: 0 },
      grid: { left: 50, right: 50, bottom: 40, top: 20, containLabel: true },
      xAxis: { type: 'category', data: months, boundaryGap: false },
      yAxis: [
        { type: 'value', name: this.variables?.estimates || 'Estimaciones', position: 'left' },
        { type: 'value', name: this.variables?.effort || 'Esfuerzo (h)', position: 'right' }
      ],
      animationDuration: 1000,
      animationEasing: 'cubicOut',
      series: [
        {
          name: this.variables?.estimates || 'Estimaciones',
          type: 'line',
          smooth: true,
          data: truncCount,
          yAxisIndex: 0,
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(99,102,241,0.35)' },
              { offset: 1, color: 'rgba(99,102,241,0.02)' }
            ])
          },
          lineStyle: { width: 3, color: '#6366F1' },
          itemStyle: { color: '#6366F1' },
          symbol: 'circle',
          symbolSize: 6
        },
        {
          name: this.variables?.effort || 'Esfuerzo (h)',
          type: 'line',
          smooth: true,
          data: truncHours,
          yAxisIndex: 1,
          lineStyle: { width: 3, color: '#F59E0B', type: 'dashed' },
          itemStyle: { color: '#F59E0B' },
          symbol: 'diamond',
          symbolSize: 6
        }
      ]
    };

    this.renderChart('chart-activity', option);
  }

  // ===== Graph 2: State Donut =====

  private drawStateDonut(data: any[]): void {
    const stateCounts: { [key: string]: number } = {};
    const stateLabels: { [key: string]: string } = {
      [this.estimationState.INCOMPLETE]: this.variables.onGoing,
      [this.estimationState.COMPLETE]: this.variables?.finisheds,
      [this.estimationState.CERTIFIED]: this.variables?.certified,
      [this.estimationState.APPROVED]: this.variables?.approved,
      [this.estimationState.REJECTED]: this.variables?.rejected,
      [this.estimationState.TO_FIX]: this.variables?.toFix,
      [this.estimationState.CLOSED]: this.variables?.Closed
    };

    const stateColors: { [key: string]: string } = {
      [this.estimationState.INCOMPLETE]: '#F59E0B',
      [this.estimationState.COMPLETE]: '#6366F1',
      [this.estimationState.CERTIFIED]: '#10B981',
      [this.estimationState.APPROVED]: '#F43F5E',
      [this.estimationState.REJECTED]: '#EC4899',
      [this.estimationState.TO_FIX]: '#94A3B8',
      [this.estimationState.CLOSED]: '#06B6D4'
    };

    data.forEach((e: any) => {
      const s = e.state || this.estimationState.INCOMPLETE;
      stateCounts[s] = (stateCounts[s] || 0) + 1;
    });

    const pieData = Object.keys(stateCounts)
      .filter(k => stateCounts[k] > 0)
      .map(k => ({
        value: stateCounts[k],
        name: stateLabels[k] || k,
        itemStyle: { color: stateColors[k] || '#94A3B8' }
      }));

    const option: echarts.EChartsOption = {
      tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
      legend: { orient: 'horizontal', bottom: 10, left: 'center' },
      animationDuration: 1000,
      animationEasing: 'cubicOut',
      series: [{
        type: 'pie',
        radius: ['42%', '70%'],
        center: ['50%', '45%'],
        avoidLabelOverlap: true,
        itemStyle: { borderRadius: 6, borderColor: '#fff', borderWidth: 2 },
        label: { show: true, formatter: '{d}%', fontSize: 11 },
        emphasis: {
          label: { show: true, fontSize: 14, fontWeight: 'bold' },
          itemStyle: { shadowBlur: 10, shadowOffsetX: 0, shadowColor: 'rgba(0,0,0,0.2)' }
        },
        data: pieData
      }],
      title: [
        {
          text: `${this.totalEstimates}`,
          left: 'center',
          top: '38%',
          textStyle: { fontSize: 28, fontWeight: 'bold', color: '#1e293b' }
        },
        {
          text: 'Total',
          left: 'center',
          top: '50%',
          textStyle: { fontSize: 12, fontWeight: 'normal', color: '#94a3b8' }
        }
      ]
    };

    this.renderChart('chart-state', option);
  }

  // ===== Graph 3: Catalog Treemap =====

  private drawCatalogTreemap(data: any[]): void {
    const catalogMap = new Map<string, { count: number; hours: number; tech: string }>();

    data.forEach((e: any) => {
      const catName = e.catalog?.catalogName || e.catalog || 'Sin catálogo';
      const tech = e.technology || '';
      if (!catalogMap.has(catName)) {
        catalogMap.set(catName, { count: 0, hours: 0, tech });
      }
      const entry = catalogMap.get(catName)!;
      entry.count++;
      entry.hours += e.totalEffortHours || 0;
    });

    const treemapData = Array.from(catalogMap.entries()).map(([name, d], i) => ({
      name: name,
      value: d.count,
      hours: d.hours,
      itemStyle: {
        color: this.COLORS[i % this.COLORS.length],
        borderColor: '#fff',
        borderWidth: 2,
        borderRadius: 4
      }
    }));

    treemapData.sort((a, b) => b.value - a.value);

    const option: echarts.EChartsOption = {
      tooltip: {
        formatter: (params: any) => {
          return `<b>${params.name}</b><br/>Estimaciones: ${params.value}<br/>Horas: ${(params.data?.hours || 0).toFixed(0)}h`;
        }
      },
      animationDuration: 1000,
      animationEasing: 'cubicOut',
      series: [{
        type: 'treemap',
        width: '95%',
        height: '85%',
        top: 20,
        roam: false,
        nodeClick: false,
        breadcrumb: { show: false },
        label: {
          show: true,
          formatter: '{b}\n{c}',
          fontSize: 12,
          fontWeight: 'bold' as any,
          color: '#fff'
        },
        itemStyle: { borderRadius: 4 },
        data: treemapData as any
      }]
    };

    this.renderChart('chart-catalog', option);
  }

  // ===== Graph 4: Precision by Catalog (Grouped Bar) =====

  private drawCatalogPrecisionBars(): void {
    if (!this.hasImputationData) return;

    const catalogData = new Map<string, { estimated: number; actual: number; count: number }>();

    this.imputations.forEach((imp: any) => {
      const est = this.estimates.find((e: any) => e.id === imp.estimateId);
      if (est && est.totalEffortHours > 0 && imp.totalActualHours != null) {
        const catName = est.catalog?.catalogName || est.catalog || 'Sin catálogo';
        if (!catalogData.has(catName)) catalogData.set(catName, { estimated: 0, actual: 0, count: 0 });
        const entry = catalogData.get(catName)!;
        entry.estimated += est.totalEffortHours;
        entry.actual += imp.totalActualHours;
        entry.count++;
      }
    });

    const sorted = Array.from(catalogData.entries())
      .filter(([, d]) => d.count > 0)
      .sort((a, b) => b[1].count - a[1].count)
      .slice(0, 10);

    if (sorted.length === 0) return;

    const categories = sorted.map(([name]) => name.length > 20 ? name.substring(0, 18) + '...' : name);
    const estimatedData = sorted.map(([, d]) => +d.estimated.toFixed(1));
    const actualData = sorted.map(([, d]) => +d.actual.toFixed(1));

    const estimLabel = this.variables?.estimatedHours || 'Estimadas';
    const actualLabel = this.variables?.actualHoursLabel || 'Reales';

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: any) => {
          const idx = params[0]?.dataIndex;
          if (idx == null || !sorted[idx]) return '';
          const [name, d] = sorted[idx];
          const deviation = ((d.actual - d.estimated) / d.estimated * 100).toFixed(1);
          const icon = Math.abs(+deviation) <= 20 ? '🟢' : Math.abs(+deviation) <= 50 ? '🟡' : '🔴';
          return `<b>${name}</b><br/>
                  ${estimLabel}: ${d.estimated.toFixed(1)}h<br/>
                  ${actualLabel}: ${d.actual.toFixed(1)}h<br/>
                  ${icon} ${+deviation > 0 ? '+' : ''}${deviation}%<br/>
                  <small>(${d.count} est.)</small>`;
        }
      },
      legend: {
        data: [estimLabel, actualLabel],
        bottom: 0,
        textStyle: { fontSize: 11, color: '#475569' }
      },
      grid: { left: 130, right: 30, bottom: 40, top: 10, containLabel: false },
      xAxis: {
        type: 'value',
        axisLabel: { formatter: '{value}h' },
        splitLine: { lineStyle: { type: 'dashed', color: '#E2E8F0' } }
      },
      yAxis: {
        type: 'category',
        data: categories,
        inverse: true,
        axisLabel: { fontSize: 11, color: '#475569' }
      },
      animationDuration: 800,
      animationEasing: 'cubicOut',
      series: [
        {
          name: estimLabel,
          type: 'bar',
          data: estimatedData,
          barWidth: 10,
          itemStyle: { color: '#6366F1', borderRadius: 2 }
        },
        {
          name: actualLabel,
          type: 'bar',
          data: actualData,
          barWidth: 10,
          itemStyle: { color: '#10B981', borderRadius: 2 }
        }
      ]
    };

    setTimeout(() => this.renderChart('chart-precision', option), 200);
  }

  // ===== Graph 5: Deviation Diverging Bar =====

  private drawDeviationChart(): void {
    if (!this.hasImputationData) return;

    const items: { name: string; estimated: number; actual: number; deviation: number; catalog: string }[] = [];

    this.imputations.forEach((imp: any) => {
      const est = this.estimates.find((e: any) => e.id === imp.estimateId);
      if (est && est.totalEffortHours > 0 && imp.totalActualHours != null) {
        const deviation = ((imp.totalActualHours - est.totalEffortHours) / est.totalEffortHours) * 100;
        items.push({
          name: est.name || est.idEstimate || 'Sin nombre',
          estimated: est.totalEffortHours,
          actual: imp.totalActualHours,
          deviation: +deviation.toFixed(1),
          catalog: est.catalog?.catalogName || est.catalog || ''
        });
      }
    });

    items.sort((a, b) => b.deviation - a.deviation);
    this.deviationTotalCount = items.length;
    const top = items.slice(0, 20);

    const names = top.map(i => i.name.length > 25 ? i.name.substring(0, 23) + '...' : i.name);
    const deviations = top.map(i => i.deviation);

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const idx = params[0]?.dataIndex;
          if (idx == null || !top[idx]) return '';
          const d = top[idx];
          const icon = Math.abs(d.deviation) <= 20 ? '🟢' : Math.abs(d.deviation) <= 50 ? '🟡' : '🔴';
          return `<b>${d.name}</b><br/>
                  Estimado: ${d.estimated.toFixed(1)}h<br/>
                  Real: ${d.actual.toFixed(1)}h<br/>
                  ${icon} Desviación: ${d.deviation > 0 ? '+' : ''}${d.deviation}%<br/>
                  <small>${d.catalog}</small>`;
        }
      },
      grid: { left: 160, right: 40, bottom: 30, top: 20, containLabel: false },
      xAxis: {
        type: 'value',
        axisLabel: { formatter: '{value}%' },
        splitLine: { lineStyle: { type: 'dashed', color: '#E2E8F0' } }
      },
      yAxis: {
        type: 'category',
        data: names,
        inverse: true,
        axisLabel: { fontSize: 11, color: '#475569' }
      },
      visualMap: {
        show: false,
        pieces: [
          { lte: -50, color: '#F43F5E' },
          { gt: -50, lte: -20, color: '#F59E0B' },
          { gt: -20, lte: 20, color: '#10B981' },
          { gt: 20, lte: 50, color: '#F59E0B' },
          { gt: 50, color: '#F43F5E' }
        ]
      },
      animationDuration: 1000,
      animationEasing: 'cubicOut',
      series: [{
        type: 'bar',
        data: deviations,
        barWidth: 16,
        itemStyle: { borderRadius: 3 },
        label: {
          show: true,
          position: 'right',
          formatter: (params: any) => `${params.value > 0 ? '+' : ''}${params.value}%`,
          fontSize: 11,
          color: '#475569'
        },
        markLine: {
          silent: true,
          symbol: 'none',
          data: [{ xAxis: 0, lineStyle: { color: '#94A3B8', type: 'solid', width: 1 } }],
          label: { show: false }
        }
      }]
    };

    setTimeout(() => this.renderChart('chart-deviation', option), 200);
  }

  // ===== Graph 6: Activity Heatmap =====

  private drawHeatmap(data: any[], mode: string): void {
    const months = this.getMonths();
    const groupMap = new Map<string, number[]>();

    data.forEach((e: any) => {
      const key = mode === 'technology' ? (e.technology || 'Other') : (e.catalog?.catalogName || e.catalog || 'Other');
      if (!groupMap.has(key)) groupMap.set(key, new Array(12).fill(0));
      const m = Number(e.createdDate?.slice(3, 5)) - 1;
      if (m >= 0 && m < 12) groupMap.get(key)![m]++;
    });

    // Sort by total and take top 10
    const sorted = Array.from(groupMap.entries())
      .map(([name, counts]) => ({ name, counts, total: counts.reduce((a, b) => a + b, 0) }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);

    const yLabels = sorted.map(s => s.name);
    const heatData: any[] = [];
    let maxVal = 0;

    sorted.forEach((s, yi) => {
      s.counts.forEach((count, xi) => {
        heatData.push([xi, yi, count]);
        maxVal = Math.max(maxVal, count);
      });
    });

    const option: echarts.EChartsOption = {
      tooltip: {
        formatter: (params: any) => {
          const [mi, gi, val] = params.data;
          return `<b>${yLabels[gi]}</b><br/>${months[mi]}: ${val} estimaciones`;
        }
      },
      grid: { left: 120, right: 40, bottom: 50, top: 10 },
      xAxis: {
        type: 'category',
        data: months,
        splitArea: { show: true },
        axisLabel: { rotate: 45 }
      },
      yAxis: {
        type: 'category',
        data: yLabels,
        splitArea: { show: true }
      },
      visualMap: {
        min: 0,
        max: maxVal || 1,
        calculable: true,
        orient: 'horizontal',
        left: 'center',
        bottom: 0,
        inRange: {
          color: ['#EEF2FF', '#C7D2FE', '#A5B4FC', '#818CF8', '#6366F1', '#4F46E5']
        }
      },
      animationDuration: 1000,
      animationEasing: 'cubicOut',
      series: [{
        type: 'heatmap',
        data: heatData,
        label: { show: true, fontSize: 10 },
        emphasis: {
          itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0,0,0,0.3)' }
        }
      }]
    };

    this.renderChart('chart-heatmap', option);
  }

  // ===== Render helper =====

  private renderChart(containerId: string, option: echarts.EChartsOption): void {
    const dom = document.getElementById(containerId);
    if (!dom) return;

    let chart = echarts.getInstanceByDom(dom);
    if (!chart) {
      chart = echarts.init(dom);
    }
    chart.setOption(option, true);

    // Resize observer for responsiveness
    const ro = new ResizeObserver(() => chart?.resize());
    ro.observe(dom);
  }
}
